// src/messaging/api.js
// メッセージ機能の API を呼ぶ共通の入口【2026-10-16】
//   宛先は /api/messages/*（nginx がメッセージ用の別プロセスへ振る）。認証は今までどおり Bearer。
//   詳しい API は docs/MESSAGING_API.md、一覧は docs/MESSAGING_API_REFERENCE.md。
//
//   使い方:
//     const r = await msgApi.get('/conversations');            // → JSON（success:false でも投げない）
//     const r = await msgApi.post('/conversations/12/messages', { text: 'こんにちは' });
//     const r = await msgApi.upload('/conversations/12/messages', file, { text: '資料です' });
//     const url = await msgApi.blobUrl(message.image.url);      // 画像は Bearer が要るので <img src> に直接書けない
//   失敗の分岐は必ず r.code で（r.message は画面にそのまま出してよい文）。

const BASE = '/api/messages';

function token() {
  try { return localStorage.getItem('token'); } catch (_) { return null; }
}

function authHeaders(extra = {}) {
  const t = token();
  return t ? { ...extra, Authorization: 'Bearer ' + t } : extra;
}

async function parse(res) {
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    const body = await res.json();
    return { status: res.status, ...body };
  }
  return { status: res.status, success: res.ok, code: res.ok ? undefined : 'HTTP_' + res.status };
}

async function call(method, path, body) {
  try {
    const res = await fetch(BASE + path, {
      method,
      headers: authHeaders(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return await parse(res);
  } catch (e) {
    return { status: 0, success: false, code: 'NETWORK', message: '通信できませんでした。電波を確かめてください' };
  }
}

/** 画像・PDF を1つ付けて送る（multipart）。fields は文字の項目 */
async function upload(path, file, fields = {}, fieldName) {
  const fd = new FormData();
  for (const [k, v] of Object.entries(fields)) {
    if (v === undefined || v === null || v === '') continue;
    fd.append(k, Array.isArray(v) ? JSON.stringify(v) : String(v));
  }
  const name = fieldName || (file && file.type === 'application/pdf' ? 'file' : 'image');
  fd.append(name, file, file.name);
  try {
    const res = await fetch(BASE + path, { method: 'POST', headers: authHeaders(), body: fd });
    return await parse(res);
  } catch (e) {
    return { status: 0, success: false, code: 'NETWORK', message: '通信できませんでした。電波を確かめてください' };
  }
}

const blobCache = new Map(); // url → objectURL（中身は変わらないので覚えておく）

/** /api/messages/images/:id などを取り出して、<img src> に使える URL にする */
async function blobUrl(url) {
  // トークンを付けて取るので、メッセージ機能の API 以外（外のサイトなど）へは送らない【2026-10-16】
  if (!url || typeof url !== 'string' || !url.startsWith('/api/messages/')) return null;
  if (blobCache.has(url)) return blobCache.get(url);
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) return null;
  const obj = URL.createObjectURL(await res.blob());
  blobCache.set(url, obj);
  return obj;
}

/** ファイルを保存させる（PDF・書き出し） */
async function download(url, fileName) {
  const res = await fetch(url.startsWith('/api/') ? url : BASE + url, { headers: authHeaders() });
  if (!res.ok) return false;
  const obj = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = obj; a.download = fileName || 'download';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(obj), 10000);
  return true;
}

/**
 * リアルタイム受信（WebSocket）。合図だけが届く（中身は来ないので、受けたら API で取り直す）。
 *   const stop = msgApi.subscribe(ev => { if (ev.type === 'message') reload(); });
 *   つながらないとき・切れたときは、呼び出し側の数秒ごとの取り直しで動き続けること。
 */
function subscribe(onEvent) {
  let ws = null, closed = false, retry = 1000, timer = null;
  const open = () => {
    if (closed) return;
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    try { ws = new WebSocket(`${proto}//${window.location.host}${BASE}/ws`); } catch (_) { schedule(); return; }
    // 認証は1回だけ送る。待ち時間を戻すのは ready を受けてから（すぐ切られる状態で連打しない）
    ws.onopen = () => { ws.send(JSON.stringify({ type: 'auth', token: token() })); };
    ws.onmessage = (e) => {
      let ev; try { ev = JSON.parse(e.data); } catch (_) { return; }
      if (ev && ev.type === 'ready') retry = 1000;
      try { onEvent(ev); } catch (_) { /* noop */ }
    };
    // 4001 = 認証できない（ログインし直しが要る）／4008 = 同じ人の接続が多すぎて古いものを閉じた → つなぎ直さない
    ws.onclose = (e) => { if (!closed && e.code !== 4001 && e.code !== 4008) schedule(); };
    ws.onerror = () => { try { ws.close(); } catch (_) { /* noop */ } };
  };
  const schedule = () => { timer = setTimeout(open, retry); retry = Math.min(retry * 2, 30000); };
  open();
  return () => { closed = true; clearTimeout(timer); try { ws && ws.close(); } catch (_) { /* noop */ } };
}

export const msgApi = {
  get: (p) => call('GET', p),
  post: (p, b) => call('POST', p, b === undefined ? {} : b),
  put: (p, b) => call('PUT', p, b === undefined ? {} : b),
  patch: (p, b) => call('PATCH', p, b === undefined ? {} : b),
  del: (p) => call('DELETE', p),
  upload, blobUrl, download, subscribe,
};

/** 機能スイッチ（/features）を1回だけ取りに行って覚えておく */
let featuresPromise = null;
export function loadFeatures(force = false) {
  if (!featuresPromise || force) {
    featuresPromise = msgApi.get('/features').then(r => (r.success ? r : { features: {}, suspended_until: null }));
  }
  return featuresPromise;
}

export const REACTIONS = ['👍', '❤️', '😆', '😮', '😢', '🙏'];
